/**
 * 个股详情页头部聚合 channel（`stock_header_detail`）：基于 {@link RealtimeSubscriber} 的订阅式通信封装。
 *
 * 协议要点：
 * - 上行订阅：`{ type: 'stock_header_detail', timestamp, param: { fullCode } }`，fullCode 归一为紧凑形式
 *   （美股保留原始大小写，A/港股小写；见 {@link normalizeStockFullCodeForSubscribe}）；
 *   个股详情页为单标的场景，param 取当前活跃的单个 fullCode。
 * - 保活：由 RealtimeSubscriber 在每次 flush 后按 5s 间隔轮询重发同一份订阅 payload。
 * - 下行：`{ businessType, data, requestId }`，其中 `data` 为 JSON 字符串，解析后形如
 *   `{ fullCode, detail: { highPrice, ... } }`；盘口字段（现价、涨跌、估值、盘口 28+ 字段）承载在 `detail` 子对象，
 *   `mapHeaderDetailToQuotePatch` 会将其透传展开到顶层。仅当解析出的 fullCode 与当前活跃订阅匹配时才分发更新。
 *
 * 与 SEC channel 的差异（刻意不进 vuex）：
 * - `stock_header_detail` 只在个股详情页头部消费，无需跨组件共享；若并入全局 `snapshotsByFullCode`
 *   会把 28+ 字段整包常驻全局 state，带来不必要的内存开销与耦合。
 * - 故本 channel 采用与 `stock_kline` 一致的「通道单例 + 本地监听器」模式：业务侧通过 hook
 *   （useStockHeaderDetailChannel）注册监听、在组件内本地持有数据，退出页面即释放。
 *
 * 下行字段命名与个股详情接口一致（后端约定），`mapHeaderDetailToQuotePatch` 采用透传式归一，
 * 业务侧按盘口字段 key（后端字段名）直接取用，无需在此做字段改名。
 *
 * @module utils/realtime/stockChannels/headerDetailChannel
 */

import { isArray, isNil, isPlainObject } from 'lodash-es';
import { normalizeStockFullCodeForSubscribe, resolveStockFullCodeKey } from '@/utils/biz/stock';
import { normalizeStringList } from '@/utils/string';
import { normalizePushData, normalizeStockDetail } from '@/utils/realtime/normalizePushData';
import { WS_MESSAGE_TYPE } from '@/utils/realtime/wsMessageType';
import { RealtimeSubscriber } from '@/utils/realtime/RealtimeSubscriber';

/** header detail channel 标识（仅供本模块内部使用） */
const HEADER_DETAIL_CHANNEL_ID = 'headerDetail';

/**
 * 上行订阅 payload：个股详情页单标的场景，param 取当前活跃的单个 fullCode。
 * @param {string[]} fullCodes 当前活跃的 fullCode 列表
 * @returns {{ type: string, timestamp: number, param: { fullCode: string } }}
 */
function buildSubscribePayload(fullCodes) {
  const [fullCode = ''] = normalizeStringList(fullCodes, {
    mapItem: normalizeStockFullCodeForSubscribe
  });
  return {
    type: WS_MESSAGE_TYPE.stock.STOCK_HEADER_DETAIL,
    timestamp: Date.now(),
    param: { fullCode }
  };
}

/**
 * 把单条服务端推送项映射为头部数据 patch（透传式归一）。
 *
 * 盘口字段承载在 `detail` 子对象中（后端约定：`{ fullCode, detail: { highPrice, ... } }`，`detail` 支持
 * 对象或 JSON 字符串），此处透传展开到顶层，字段命名与个股详情接口一致，业务侧按盘口字段 key（后端字段名）
 * 直接取用，无需字段改名。
 * @param {Record<string, unknown> | undefined} item 单条下行明细
 * @returns {Record<string, unknown>}
 */
function mapHeaderDetailToQuotePatch(item) {
  if (!isPlainObject(item)) {
    return {};
  }
  // 盘口数据在 detail 子对象，展开到顶层供业务按盘口字段 key 直接取用
  const patch = { ...item, ...normalizeStockDetail(item.detail) };
  delete patch.detail;
  if (!isNil(patch.lastPrice)) {
    patch.price = patch.lastPrice;
  }
  return patch;
}

/**
 * 下行解析：兼容 stockInfo 数组 / 数组 / 单对象三种形态，按活跃 fullCodes 收敛为 `{ fullCode, patch }`。
 * @param {unknown} data 下行消息 data 段
 * @param {Iterable<string>} activeFullCodes 当前活跃订阅的 fullCode 集合
 * @returns {Array<{ fullCode: string, patch: object }>}
 */
function parseHeaderDetailPush(data, activeFullCodes) {
  const normalized = normalizePushData(data);
  let list;
  if (isArray(normalized?.stockInfo)) {
    list = normalized.stockInfo;
  } else if (isArray(normalized)) {
    list = normalized;
  } else {
    list = [normalized];
  }
  const rows = [];
  for (const item of list) {
    if (!isPlainObject(item)) {
      continue;
    }
    const fullCode = resolveStockFullCodeKey(item.fullCode || item.stockCode, activeFullCodes);
    if (!fullCode) {
      continue;
    }
    rows.push({ fullCode, patch: mapHeaderDetailToQuotePatch(item) });
  }
  return rows;
}

/**
 * 个股头部聚合订阅器：维护 per-fullCode 引用计数 + 本地监听器，内部组合 {@link RealtimeSubscriber} 完成 hub 通信。
 * 数据不进 vuex，由各监听器（组件内 hook）本地持有。
 */
export class HeaderDetailChannelSubscriber {
  /** channel 标识 */
  static id = HEADER_DETAIL_CHANNEL_ID;

  constructor() {
    /** @type {Map<string, number>} fullCode -> 引用计数 */
    this._refCounts = new Map();

    /** @type {Map<(patch: object) => void, string>} listener -> 关注的 fullCode */
    this._listeners = new Map();

    this._subscriber = new RealtimeSubscriber({
      topic: WS_MESSAGE_TYPE.stock.STOCK_HEADER_DETAIL,
      buildSubscribePayload: () => buildSubscribePayload(this._activeFullCodes()),
      onPush: (data) => {
        const rows = parseHeaderDetailPush(data, this._activeFullCodes());
        for (const { fullCode, patch } of rows) {
          this._emit(fullCode, patch);
        }
      }
    });
  }

  /**
   * 注册下行监听：仅接收所关注 fullCode 的 patch。
   * @param {string} fullCode 关注的 fullCode（须与 subscribe 传入的一致）
   * @param {(patch: object) => void} listener 监听器
   * @returns {void}
   */
  addListener(fullCode, listener) {
    if (typeof listener !== 'function' || !fullCode) {
      return;
    }
    this._listeners.set(listener, fullCode);
  }

  /**
   * 移除下行监听。
   * @param {(patch: object) => void} listener 监听器
   * @returns {void}
   */
  removeListener(listener) {
    this._listeners.delete(listener);
  }

  /**
   * 把某 fullCode 的 patch 分发给关注它的监听器。
   * @param {string} fullCode
   * @param {object} patch
   * @returns {void}
   */
  _emit(fullCode, patch) {
    for (const [listener, code] of this._listeners.entries()) {
      if (code !== fullCode) {
        continue;
      }
      try {
        listener(patch);
      } catch (err) {
        console.error('[HeaderDetailChannel] listener threw:', err);
      }
    }
  }

  /**
   * 增加订阅：每个 fullCode 引用计数 +1；首次进入活跃态时 install subscriber，否则 requestFlush。
   * @param {string[]} fullCodes 待订阅的 fullCode 列表
   * @returns {void}
   */
  subscribe(fullCodes) {
    const codes = normalizeStringList(fullCodes);
    if (!codes.length) {
      return;
    }
    const wasActive = this._subscriber.isInstalled();
    let needFlush = false;
    for (const code of codes) {
      const cur = this._refCounts.get(code) || 0;
      this._refCounts.set(code, cur + 1);
      if (cur === 0) {
        needFlush = true;
      }
    }
    if (!wasActive) {
      this._subscriber.install();
      return;
    }
    if (needFlush) {
      this._subscriber.requestFlush();
    }
  }

  /**
   * 减少订阅：每个 fullCode 引用计数 -1；完全无订阅时 destroy subscriber 释放 hub 订阅与 timer。
   * @param {string[]} fullCodes 待退订的 fullCode 列表
   * @returns {void}
   */
  unsubscribe(fullCodes) {
    const codes = normalizeStringList(fullCodes);
    if (!codes.length) {
      return;
    }
    let needFlush = false;
    for (const code of codes) {
      const cur = this._refCounts.get(code) || 0;
      if (cur <= 0) {
        continue;
      }
      const next = cur - 1;
      if (next <= 0) {
        this._refCounts.delete(code);
        needFlush = true;
      } else {
        this._refCounts.set(code, next);
      }
    }
    if (!needFlush) {
      return;
    }
    if (this._refCounts.size === 0) {
      this._subscriber.destroy();
      return;
    }
    this._subscriber.requestFlush();
  }

  /**
   * 销毁：用于热替换 / 单测；释放 hub 订阅、timer、refCount 与监听器。
   * @returns {void}
   */
  destroy() {
    this._subscriber.destroy();
    this._refCounts.clear();
    this._listeners.clear();
  }

  /**
   * 当前仍有订阅的 fullCode 列表（字典序）。
   * @returns {string[]}
   */
  _activeFullCodes() {
    const out = [];
    for (const [code, n] of this._refCounts.entries()) {
      if (n > 0) {
        out.push(code);
      }
    }
    return out.sort();
  }
}

/** @type {HeaderDetailChannelSubscriber | null} */
let headerDetailChannelSubscriberSingleton = null;

/**
 * 获取 header detail channel 的模块级 lazy 单例。
 * @returns {HeaderDetailChannelSubscriber}
 */
export function getHeaderDetailChannelSubscriber() {
  if (!headerDetailChannelSubscriberSingleton) {
    headerDetailChannelSubscriberSingleton = new HeaderDetailChannelSubscriber();
  }
  return headerDetailChannelSubscriberSingleton;
}
